import { toast } from 'sonner';
import { notifyAuthExpired } from '@/lib/authSession';

const API_URL = '/api';

// ============================================================================
// API Error
// ============================================================================

export class ApiError extends Error {
  status: number;
  statusText: string;
  details: unknown;

  constructor(response: Response, details: unknown) {
    const message =
      (typeof details === 'string' && details.length > 0 && details) ||
      (details && typeof details === 'object' && 'message' in details
        ? String((details as { message?: string }).message)
        : undefined) ||
      (details && typeof details === 'object' && 'error' in details
        ? String((details as { error?: string }).error)
        : undefined) ||
      response.statusText ||
      `Request failed with status ${response.status}`;
    super(message);
    this.name = 'ApiError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.details = details;
  }
}

// ============================================================================
// Auth Helpers
// ============================================================================

export const AUTH_TOKEN_KEY = 'authToken';

export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

function getAuthHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = { ...additionalHeaders };
  if (token && !headers.Authorization) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * The request never reached the server (offline, DNS failure, server down).
 * Distinct from ApiError so callers can tell connectivity loss apart from a
 * real server response.
 */
export class NetworkError extends Error {
  constructor(message = 'Unable to connect to server. Please check that the server is running.') {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

// ============================================================================
// API Fetch Options
// ============================================================================

export interface ApiFetchOptions {
  path: string;
  method?: string;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  formData?: FormData;
}

// ============================================================================
// Low-level Fetch (returns Response)
// ============================================================================

export async function apiFetch(options: ApiFetchOptions): Promise<Response> {
  const { path, method = 'GET', query, body, signal, headers = {}, formData } = options;

  const url = new URL(`${API_URL}${path}`, window.location.origin);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) {
        continue;
      }
      url.searchParams.set(key, String(value));
    }
  }

  const init: RequestInit = { method, signal };

  if (formData) {
    init.body = formData;
    init.headers = getAuthHeaders(headers);
  } else if (body !== undefined && body !== null) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
    init.headers = getAuthHeaders({ 'Content-Type': 'application/json', ...headers });
  } else {
    init.headers = getAuthHeaders(headers);
  }

  try {
    const response = await fetch(url.toString(), init);

    // 401 is "your session is over" — drop the token and let the router send
    // the user to /login keeping the path they were on. 403 is "you are signed
    // in but this action isn't yours": the server is the real gate, so a 403
    // means the UI rendered a control it should have hidden, and the user is
    // told rather than watching a button do nothing.
    if (response.status === 401) {
      notifyAuthExpired();
    } else if (response.status === 403) {
      toast.error("You don't have permission");
    }

    return response;
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      throw new NetworkError();
    }
    throw error;
  }
}

// ============================================================================
// High-level Request (parses response, throws on error)
// ============================================================================

export async function apiRequest<T = unknown>(options: ApiFetchOptions): Promise<T> {
  const response = await apiFetch(options);

  const contentType = response.headers.get('content-type') || '';
  let data: unknown = null;

  try {
    if (contentType.includes('application/json')) {
      data = await response.json();
    } else if (contentType.startsWith('text/')) {
      data = await response.text();
    } else if (response.status === 204 || response.status === 205) {
      data = null;
    } else if (response.ok) {
      data = await response.arrayBuffer();
    } else {
      data = await response.text();
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    if (response.ok) {
      console.warn('Failed to parse API response', error);
    }
  }

  if (!response.ok) {
    if (
      typeof data === 'string' &&
      (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html'))
    ) {
      data = 'Unable to connect to server. Please check that the server is running.';
    }
    throw new ApiError(response, data);
  }

  return data as T;
}

// ============================================================================
// Event Stream URL Helper
// ============================================================================

/**
 * EventSource can't set request headers, so the JWT rides as a query param
 * (the orders server accepts `access_token` on /api/events). On a manual
 * reconnect we also pass the last-seen event id so the server replays
 * everything newer.
 */
export function getEventsUrl(lastEventId?: string | null): string {
  const url = new URL(`${API_URL}/events`, window.location.origin);
  if (lastEventId) {
    url.searchParams.set('lastEventId', lastEventId);
  }
  const token = getAuthToken();
  if (token) {
    url.searchParams.set('access_token', token);
  }
  return url.toString();
}
